import React, { useEffect, useMemo, useState, useRef } from "react";
import "../styles/executive-responsive.css";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { DateCarousel } from "../components/DateCarousel";
import { CitationHoverCard } from "../components/CitationHoverCard";
import { authFetch } from "../../../services/authFetch";
import config from "../../../config";
import { getParticipantColor } from "../../../utils/participantColors";
import { participantsCache } from "./ParticipantList";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { X, Download } from "lucide-react";

// ── Markdown rendering ─────────────────────────────────────────

function renderInlineMarkdown(text: string, keyPrefix: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const boldPattern = /\*\*(.+?)\*\*/g;
  let lastIdx = 0;
  let m;
  while ((m = boldPattern.exec(text)) !== null) {
    if (m.index > lastIdx) {
      parts.push(<span key={`md-${keyPrefix}-${lastIdx}`}>{text.slice(lastIdx, m.index)}</span>);
    }
    parts.push(
      <strong key={`b-${keyPrefix}-${m.index}`} style={{ color: "#fff", fontWeight: 700 }}>
        {m[1]}
      </strong>,
    );
    lastIdx = boldPattern.lastIndex;
  }
  if (lastIdx < text.length) {
    parts.push(<span key={`md-${keyPrefix}-end`}>{text.slice(lastIdx)}</span>);
  }
  return parts.length > 0 ? parts : [<span key={`md-${keyPrefix}-full`}>{text}</span>];
}

type SummaryType = "daily" | "lifetime";

const WINDOW_OPTIONS: { value: SummaryType; labelKey: string }[] = [
  { value: "daily", labelKey: "executive.window.daily" },
  { value: "lifetime", labelKey: "executive.window.lifetime" },
];

interface Summary {
  id: string;
  type: SummaryType;
  content: string;
  generated_at: string;
  window_start: string | null;
  window_end: string;
  source_count: number;
  model: string | null;
}

interface Props {
  // Normalmente un UUID string, pero ExecutiveLayout (legacy) a veces pasa el
  // objeto participante completo; el componente normaliza ambos casos.
  participantId: string | { id?: string; [key: string]: any };
  projectId: string;
  participantData?: any; // objeto participante ya cargado
  onBack: () => void;
}

export const ParticipantSummaryDetail: React.FC<Props> = ({
  participantId: rawParticipantId,
  projectId,
  participantData,
  onBack,
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [windowType, setWindowType] = useState<SummaryType>("daily");
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportGlossary, setExportGlossary] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const [summaries, setSummaries] = useState<Summary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCitation, setActiveCitation] = useState<{
    text: string;
    id: string;
    x: number;
    y: number;
  } | null>(null);
  const [participant, setParticipant] = useState<any>(participantData || null);

  // Resolve a valid UUID participant ID — handle both string and object (old ExecutiveLayout passes object)
  const isUUID = (s: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
  const participantId = (() => {
    if (typeof rawParticipantId === "string" && isUUID(rawParticipantId)) return rawParticipantId;
    if (typeof rawParticipantId === "object" && rawParticipantId?.id && isUUID(rawParticipantId.id))
      return rawParticipantId.id;
    if (participantData?.id && isUUID(participantData.id)) return participantData.id;
    return null;
  })();

  // If rawParticipantId is an object, use it as participant data too
  const effectiveParticipantData =
    participantData ||
    (typeof rawParticipantId === "object" && rawParticipantId?.id ? rawParticipantId : null);

  // If ID is invalid, clean the URL immediately and go back to list
  useEffect(() => {
    if (!participantId) {
      console.warn("🔑 Invalid participant ID, cleaning URL:", rawParticipantId);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("participant");
          return next;
        },
        { replace: true },
      );
    }
  }, [participantId]);

  // Load participant data
  useEffect(() => {
    if (effectiveParticipantData) {
      setParticipant(effectiveParticipantData);
      return;
    }
    if (!participantId || !projectId) return;
    // Check cache first
    const cached = participantsCache[projectId];
    if (cached) {
      const p = cached.find((pp: any) => pp.id === participantId);
      if (p) {
        setParticipant(p);
        return;
      }
    }
    // Fetch from API
    let cancelled = false;
    authFetch(`${config.API_URL}/api/projects/${projectId}/participants`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || !data.participants) return;
        participantsCache[projectId] = data.participants;
        const p = data.participants.find((pp: any) => pp.id === participantId);
        if (p) setParticipant(p);
      })
      .catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [participantId, projectId, effectiveParticipantData]);

  // Don't render anything if ID is invalid
  if (!participantId) return null;

  const selectedDate = searchParams.get("date");
  const setSelectedDate = (date: string | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (date) next.set("date", date);
        else next.delete("date");
        return next;
      },
      { replace: true },
    );
  };
  // Date navigation
  const recordingDays = useMemo(() => {
    const days = [];
    let current = new Date("2026-05-17T12:00:00-06:00");
    const todayMXString = new Date().toLocaleString("en-US", {
      timeZone: "America/Mexico_City",
    });
    const todayMXDate = new Date(todayMXString);
    todayMXDate.setHours(23, 59, 59, 999);

    let dayNumber = 1;
    while (current <= todayMXDate) {
      const mxDateString = current
        .toLocaleString("sv-SE", { timeZone: "America/Mexico_City" })
        .slice(0, 10);
      const todayIsoMX = todayMXDate
        .toLocaleString("sv-SE", { timeZone: "America/Mexico_City" })
        .slice(0, 10);
      days.push({
        iso: mxDateString,
        date: new Date(current),
        isToday: mxDateString === todayIsoMX,
        dayNumber,
      });
      current.setDate(current.getDate() + 1);
      dayNumber++;
    }
    return days;
  }, []);

  useEffect(() => {
    if (!activeCitation) return;
    const hide = () => setActiveCitation(null);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("click", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("click", hide);
    };
  }, [activeCitation]);

  useEffect(() => {
    let cancelled = false;
    if (!participantId) {
      setError("ID de participante no válido");
      setLoading(false);
      return;
    }
    (async () => {
      // Only set loading if we don't have any summaries yet to avoid flashing
      if (summaries.length === 0) setLoading(true);
      setError(null);
      try {
        let url = `${config.API_URL}/api/participants/${participantId}/summaries/latest?type=${windowType}`;
        if (selectedDate) url += `&date=${selectedDate}`;
        const res = await authFetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (!cancelled) setSummaries(body.summary ? [body.summary] : []);
      } catch (e: any) {
        if (!cancelled) setError(e.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [participantId, windowType, selectedDate]);

  const latest = summaries[0];

  const locale = useMemo(() => navigator.language || "es-ES", []);

  const renderContent = (fullContent: string) => {
    if (!fullContent || typeof fullContent !== "string") return null;

    let mainText = fullContent;
    const splitRegex =
      /(?:\n#{1,3}\s*|\n---*\s*|\n\s*)*(?:GLOSARIO(?: DE CITAS)?|FUENTES|CITAS):?(?:\s*---*)?\n/i;
    const parts = fullContent.split(splitRegex);
    if (parts.length > 1) {
      mainText = parts[0].trim();
    }

    // Fallback: scan the entire fullContent for lines starting with a citation tag.
    // This catches glossary entries even if the header was missing or the split failed.
    const glossaryMap = new Map<string, string>();
    const entryRegex =
      /(?:^|\n)\s*(?:(?:[-*]|\d+\.)\s*)?\[((?:LOG|TRANS|OTF)-[^\]]+)\]\s*(?:[:-]\s*)?([\s\S]*?)(?=\n\s*(?:(?:[-*]|\d+\.)\s*)?\[(?:LOG|TRANS|OTF)-|$)/g;

    let match;
    while ((match = entryRegex.exec(fullContent)) !== null) {
      let text = match[2].trim();
      // Cut off if it hits another major section or header
      text = text.split(/\n\s*(?:---|###)\s*(?:GLOSARIO|FUENTES|CITAS)/i)[0].trim();
      if (text.length > 3) {
        glossaryMap.set(match[1], text);
      }
    }

    // Helper: find glossary entry by exact key or partial UUID prefix match
    const findGlossaryText = (citKey: string): string => {
      // 1. Exact match
      if (glossaryMap.has(citKey)) return glossaryMap.get(citKey)!;
      // 2. Partial match: citKey might be truncated (LOG-4d3a1f49 vs LOG-4d3a1f49-6851-4da9-...)
      for (const [gKey, gVal] of glossaryMap) {
        if (gKey.startsWith(citKey)) return gVal;
      }
      // 3. Reverse: glossary key might be truncated while citKey is full
      for (const [gKey, gVal] of glossaryMap) {
        if (citKey.startsWith(gKey)) return gVal;
      }
      return "Fuente original";
    };

    let seqCounter = 1;
    const citationNumMap = new Map<string, number>();
    const lines = mainText.split("\n");
    const elements: React.ReactNode[] = [];

    lines.forEach((line, lineIdx) => {
      if (!line.trim()) {
        elements.push(<br key={`br-${lineIdx}`} />);
        return;
      }

      // Headings
      const headingMatch = line.match(/^#{1,3}\s+(.+)$/);
      const capsHeadingMatch = !headingMatch && line.match(/^([A-ZÁÉÍÓÚÑ0-9\s()]+):?$/);

      const isHeading = headingMatch || capsHeadingMatch;
      const headingText = headingMatch
        ? headingMatch[1].trim()
        : capsHeadingMatch
          ? capsHeadingMatch[1].trim()
          : null;

      if (isHeading) {
        elements.push(
          <div
            key={`h-${lineIdx}`}
            style={{
              fontSize: "1rem",
              fontWeight: 800,
              color: "var(--color-accent-primary, #EC4899)",
              marginTop: "1.2em",
              marginBottom: "0.5em",
              letterSpacing: "0.3px",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              paddingBottom: "4px",
            }}
          >
            {headingText}
          </div>,
        );
        return;
      }

      // Check for [HH:MM] prefix to build sub-timeline layout
      const timeMatch = line.match(/^[-*]?\s*\[?(\d{2}:\d{2}(?::\d{2})?)\]?\s*(.*)$/);
      let timeStr = null;
      let lineContent = line;

      if (timeMatch) {
        timeStr = timeMatch[1];
        lineContent = "- " + timeMatch[2];
      }

      // Inline citations — supports grouped: [LOG-abc, LOG-def] and single: [LOG-uuid]
      // First, pre-process lineContent to split grouped citations into individual ones
      let processedLine = lineContent.replace(
        /\[((?:(?:LOG|TRANS|OTF)-[a-f0-9-]+(?:,\s*)?)+)\]/gi,
        (fullMatch, inner) => {
          // Split "LOG-abc, LOG-def, LOG-ghi" into individual "[LOG-abc] [LOG-def] [LOG-ghi]"
          const ids = inner.split(/,\s*/).filter((s: string) => s.trim());
          if (ids.length <= 1) return fullMatch; // Single citation, keep as-is
          return ids.map((id: string) => `[${id.trim()}]`).join(" ");
        },
      );

      const inlinePattern = /\[((?:LOG|TRANS|OTF)-[^\],]+)\]/g;
      const inlineParts: React.ReactNode[] = [];
      let lastIdx = 0;
      let inlineMatch;

      while ((inlineMatch = inlinePattern.exec(processedLine)) !== null) {
        if (inlineMatch.index > lastIdx) {
          inlineParts.push(
            ...renderInlineMarkdown(
              processedLine.slice(lastIdx, inlineMatch.index),
              `${lineIdx}-${lastIdx}`,
            ),
          );
        }
        const citKey = inlineMatch[1].trim();
        if (!citationNumMap.has(citKey)) citationNumMap.set(citKey, seqCounter++);
        const seqNum = citationNumMap.get(citKey);
        const citText = findGlossaryText(citKey);

        const isOtf = citKey.startsWith("OTF-");
        const isTrans = citKey.startsWith("TRANS-");
        const bgColor = isOtf ? "#06b6d4" : isTrans ? "#8B5CF6" : "#EC4899";

        inlineParts.push(
          <sup
            key={`cit-${lineIdx}-${inlineMatch.index}`}
            onMouseEnter={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setActiveCitation({
                text: citText,
                id: citKey,
                x: rect.left,
                y: rect.top - 10,
              });
            }}
            onMouseLeave={() => setActiveCitation(null)}
            style={{
              color: "#fff",
              background: bgColor,
              borderRadius: "3px",
              padding: "1px 5px",
              fontSize: "0.65rem",
              marginLeft: "4px",
              cursor: "pointer",
              userSelect: "none",
              boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
            }}
          >
            {seqNum}
          </sup>,
        );
        lastIdx = inlinePattern.lastIndex;
      }

      if (lastIdx < processedLine.length) {
        inlineParts.push(...renderInlineMarkdown(processedLine.slice(lastIdx), `end-${lineIdx}`));
      }

      if (timeStr) {
        elements.push(
          <div
            key={`line-${lineIdx}`}
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "flex-start",
              marginBottom: "0.5em",
            }}
          >
            <div
              style={{
                width: "40px",
                flexShrink: 0,
                fontSize: "0.75rem",
                color: "var(--color-text-muted)",
                textAlign: "right",
                fontFamily: "monospace",
                paddingTop: "4px",
              }}
            >
              {timeStr}
            </div>
            <div style={{ flex: 1 }}>{inlineParts}</div>
          </div>,
        );
      } else {
        elements.push(
          <div key={`line-${lineIdx}`} style={{ marginBottom: "0.5em" }}>
            {inlineParts}
          </div>,
        );
      }
    });

    return elements;
  };

  const exportToPdf = async () => {
    if (!latest) return;
    const dateStr = selectedDate || new Date().toISOString().split("T")[0];

    try {
      setExportingPdf(true);
      const url = `${config.API_URL}/api/participants/${participantId}/summaries/export-pdf?type=${windowType}&date=${dateStr}&glossary=${exportGlossary}`;

      const res = await authFetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      const typeLabel = windowType === "lifetime" ? "GLOBAL" : "DIARIO";
      const participantName = participant?.name || participant?.alias || "Participante";
      a.download = `Resumen-${participantName.replace(/\s+/g, "_")}-${typeLabel}-${dateStr}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      setExportOpen(false);
    } catch (e: any) {
      console.error("Error al exportar PDF:", e);
      alert("Error al exportar PDF: " + e.message);
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="participant-detail-container" style={{ maxWidth: "1200px", margin: "0 auto" }}>
      <div style={{ position: "relative", marginBottom: "20px" }}>
        <DateCarousel
          recordingDays={recordingDays}
          selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
        />
      </div>

      <div
        className="participant-detail-toolbar"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "transparent",
            border: "1px solid var(--color-panel-border)",
            color: "var(--color-text-muted)",
            padding: "6px 12px",
            borderRadius: "var(--border-radius-pill)",
            cursor: "pointer",
            fontSize: "0.75rem",
            fontWeight: 600,
          }}
        >
          <i className="bx bx-arrow-back"></i> {t("executive.backToList")}
        </button>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {WINDOW_OPTIONS.map((opt) => {
            const active = opt.value === windowType;
            return (
              <button
                key={opt.value}
                onClick={() => setWindowType(opt.value)}
                style={{
                  background: active ? "#EC4899" : "rgba(255,255,255,0.03)",
                  color: active ? "#fff" : "var(--color-text-muted)",
                  border: `1px solid ${active ? "#EC4899" : "var(--color-panel-border)"}`,
                  padding: "6px 14px",
                  borderRadius: "var(--border-radius-pill)",
                  cursor: "pointer",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                }}
              >
                {t(opt.labelKey)}
              </button>
            );
          })}
        </div>
        {/* Export to PDF button */}
        {latest && (
          <Button
            className="export-btn"
            variant="outline"
            size="sm"
            onClick={() => setExportOpen(true)}
            style={{
              borderColor: "rgba(236,72,153,0.4)",
              color: "#EC4899",
              background: "rgba(236,72,153,0.1)",
              marginLeft: "auto",
            }}
          >
            <Download size={16} className="mr-2" />
            Exportar PDF
          </Button>
        )}
      </div>

      <div
        className="participant-detail-identity"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          marginBottom: 24,
          paddingLeft: 8,
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "#1a1a1a",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: participant
              ? `2px solid ${getParticipantColor(participant.colorId, participant.customColor).border}`
              : "2px solid rgba(255,255,255,0.15)",
            flexShrink: 0,
          }}
        >
          {participant?.avatarUrl ? (
            <img
              src={participant.avatarUrl}
              alt={participant.name || ""}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <i
              className="bx bx-user"
              style={{
                fontSize: 22,
                color: participant
                  ? getParticipantColor(participant.colorId, participant.customColor).bg
                  : "rgba(255,255,255,0.3)",
              }}
            />
          )}
        </div>
        <div
          style={{
            background: participant
              ? getParticipantColor(participant.colorId, participant.customColor).bg
              : "rgba(255,255,255,0.1)",
            color: "#fff",
            padding: "5px 14px",
            borderRadius: "6px",
            fontWeight: 700,
            fontSize: "0.9rem",
            letterSpacing: "1px",
            textTransform: "uppercase",
          }}
        >
          {participant ? (
            <>
              {participant.name}
              {participant.alias && participant.alias !== participant.name && (
                <span
                  style={{
                    opacity: 0.75,
                    marginLeft: 8,
                    fontSize: "0.78rem",
                    fontWeight: 500,
                  }}
                >
                  ({participant.alias})
                </span>
              )}
            </>
          ) : (
            <span style={{ opacity: 0.5 }}>…</span>
          )}
        </div>
      </div>

      {loading && (
        <div style={{ padding: 40, color: "var(--color-text-muted)" }}>
          {t("executive.loading")}
        </div>
      )}
      {error && <div style={{ padding: 40, color: "var(--color-accent-danger)" }}>{error}</div>}

      {!loading && !error && !latest && (
        <div
          style={{
            padding: 40,
            color: "var(--color-text-muted)",
            textAlign: "center",
          }}
        >
          {t("executive.noSummaryForWindow")}
        </div>
      )}

      {latest && (
        <article
          className="participant-detail-article"
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid var(--color-panel-border)",
            borderRadius: "var(--border-radius-md)",
            padding: 24,
            marginBottom: 24,
          }}
        >
          <header
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: 14,
              color: "var(--color-text-muted)",
              fontSize: "0.75rem",
            }}
          >
            <span style={{ letterSpacing: "1px", textTransform: "uppercase" }}>
              {t("executive.latestSummary")}
            </span>
            <span>
              {new Date(latest.generated_at).toLocaleString(locale)}
              {latest.source_count ? ` · ${latest.source_count} ${t("executive.sources")}` : ""}
            </span>
          </header>
          <div ref={contentRef} style={{ lineHeight: 1.7, fontSize: "0.95rem" }}>
            {renderContent(latest.content)}
          </div>
        </article>
      )}

      {activeCitation && <CitationHoverCard citation={activeCitation} />}

      {/* Export Dialog Modal */}
      {exportOpen && (
        <div className="export-dialog-modal fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="export-dialog-content bg-[#1f1f1f] border border-[#333] rounded-xl shadow-2xl w-full max-w-md p-6 relative flex flex-col">
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-3 right-3 text-gray-400 hover:text-white"
              onClick={() => setExportOpen(false)}
            >
              <X size={18} />
            </Button>

            <h2 className="text-lg font-bold text-white mb-2">Exportar Resumen de Participante</h2>

            <div className="flex flex-col gap-4 py-2 text-sm text-gray-300">
              <p>
                Selecciona las opciones para el PDF. Se exportará el resumen{" "}
                <strong>{windowType === "lifetime" ? "Global" : "Diario"}</strong> para la fecha
                seleccionada.
              </p>

              <div className="flex flex-col gap-3 mt-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={exportGlossary}
                    onCheckedChange={(c) => setExportGlossary(!!c)}
                  />
                  <div>
                    <div className="font-medium text-white">Incluir Glosario de Citas</div>
                    <div className="text-xs text-gray-400">
                      Añade un anexo con las transcripciones e historias vinculadas a cada cita
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className="export-dialog-actions flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setExportOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={exportToPdf}
                disabled={exportingPdf}
                style={{
                  background: "#EC4899",
                  color: "white",
                  border: "none",
                }}
              >
                {exportingPdf ? "Generando..." : "Descargar PDF"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
